import Vue from 'vue'

import type { Nullable, MediaGroup } from '@/types'
import { getCurrentInstance, mountVueToBody } from '@/util'
import { useMediaGroupService, useLoading, useConfirm } from '@/use'

export default function useMediaGroupCRUD() {
  const instance = getCurrentInstance()
  const { service } = useMediaGroupService()
  const { setLoading } = useLoading()
  const { confirm } = useConfirm()

  async function create(): Promise<Nullable<MediaGroup>> {
    const { default: options } = await import(
      '@/components/MediaGroupForm/CreateMediaGroupFormDialog.vue'
    )
    return new Promise((resolve) => {
      const c = new Vue({
        extends: options,
        parent: instance.proxy.$root,
      })
      const destroy = mountVueToBody(c)
      c.$once('success', (group: MediaGroup) => {
        resolve(group)
        destroy()
      })
      c.$once('fail', () => {
        resolve(null)
        destroy()
      })
    })
  }

  async function edit(id: MediaGroup['id']): Promise<Nullable<MediaGroup>> {
    setLoading(true)
    try {
      const group = await service.get(id)
      const { default: options } = await import(
        '@/components/MediaGroupForm/EditMediaGroupFormDialog.vue'
      )
      return new Promise((resolve) => {
        const c = new Vue({
          extends: options,
          parent: instance.proxy.$root,
          propsData: { group },
        })
        const destroy = mountVueToBody(c)
        c.$once('success', (group: MediaGroup) => {
          resolve(group)
          destroy()
        })
        c.$once('fail', () => {
          resolve(null)
          destroy()
        })
      })
    } catch (e) {
      window.alert(e)
      return null
    } finally {
      setLoading(false)
    }
  }

  async function del(id: number) {
    if (
      (await confirm(
        'Are you sure you would like to delete this playlist?'
      )) === false
    ) {
      return
    }
    await service.delete(id)
  }

  return {
    create,
    edit,
    del,
  }
}
